import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { addComment, getComments } from "../../actions/comment.actions";
import { UidContext } from "../App.Context";
import ImageProfil from "../Profil/ImageProfil";
import { DateParser, isEmpty } from "../utils/Utils";
import UpdateComment from "./UpdateComment";
import { MentionsInput, Mention } from "react-mentions";
import { CornerDownRight, X } from "lucide-react";
import parse from "html-react-parser";

const sortByDate = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);

const CardComments = ({ post, socket }) => {
  const [commentaire, setComment] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const uid = useContext(UidContext);
  const usersData = useSelector((state) => state.usersReducer);
  const userData = useSelector((state) => state.userReducer);
  const commentsData = useSelector((state) => state.commentReducer);
  const dispatch = useDispatch();

  const getUserByFullName = (username) => {
    const u = (username || "").toLowerCase().trim();
    const found = usersData.find(
      (user) =>
        `${user.firstname} ${user.name}`.toLowerCase().trim() === u || // nouveau format
        user.name.toLowerCase() === u || // ancien format (nom seul)
        user.firstname.toLowerCase() === u
    );
    return found
      ? { id: found.id, name: found.name, firstname: found.firstname }
      : null;
  };

  const userName = (id) => {
    const u = usersData.find((x) => x.id === id);
    return u ? `${u.firstname} ${u.name}` : "";
  };

  const parseMention = (text) => {
    const mentionRegex = /@\[([^\]]+)\]\(\d+\)/g;
    let updatedText = text;

    const matches = text.match(mentionRegex);
    if (matches) {
      matches.forEach((match) => {
        const fullName = match.match(/@\[([^\]]+)\]/)[1];
        const user = getUserByFullName(fullName);
        if (user) {
          const link = `<a href="/userprofil/${user.id}"><strong class="blue-mention">@${user.firstname} ${user.name}</strong></a>`;
          updatedText = updatedText.replace(match, `${link} `);
        }
      });
    }
    return updatedText;
  };

  // Poste un commentaire (parentId = null) ou une réponse
  const submitText = (text, parentId) => {
    const formatted = parseMention(text);

    // IDs mentionnés depuis le markup @[display](id) : robuste (accents,
    // noms composés…), dédupliqués et sans soi-même.
    const mentionIds = [];
    const mentionRegex = /@\[[^\]]+\]\((\d+)\)/g;
    let match;
    while ((match = mentionRegex.exec(text))) {
      const id = Number(match[1]);
      if (id && !mentionIds.includes(id)) mentionIds.push(id);
    }

    return dispatch(addComment(formatted, post.id, parentId, mentionIds)).then(() => {
      dispatch(getComments());
      // Temps réel (en plus de la notif persistée créée côté backend).
      mentionIds.forEach((id) => {
        const u = usersData.find((x) => x.id === id);
        if (u) {
          socket?.emit("sendNotification", {
            senderName: userData.name,
            receiverName: u.name,
            receiverId: u.id,
            type: "mention_post",
          });
        }
      });
    });
  };

  const handleComment = (e) => {
    e.preventDefault();
    if (commentaire) submitText(commentaire, null).then(() => setComment(""));
  };

  const closeReply = () => {
    setReplyTo(null);
    setReplyText("");
  };

  const handleReply = (e, parentId) => {
    e.preventDefault();
    if (replyText.trim()) submitText(replyText, parentId).then(() => closeReply());
  };

  const commDuPost = Object.values(commentsData).filter(
    (comment) => comment.postId === post.id
  );
  const topComments = commDuPost.filter((c) => !c.parentId).sort(sortByDate);
  const repliesOf = (id) =>
    commDuPost.filter((c) => c.parentId === id).sort(sortByDate);
  const replyComment = replyTo ? commDuPost.find((c) => c.id === replyTo) : null;

  useEffect(() => {
    const checkAdmin = () => {
      if (uid === userData.id && userData.isAdmin) setIsAdmin(true);
    };
    checkAdmin();
  }, [uid, userData.id, userData.isAdmin]);

  useEffect(() => {
    if (!replyTo) return;
    const onEsc = (e) => e.key === "Escape" && closeReply();
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [replyTo]);

  const renderComment = (comment, isReply = false) => (
    <div
      key={comment.id}
      className={`comment-container ${comment.author === comment.userId ? "user" : ""} ${
        isReply ? "is-reply" : ""
      }`}
    >
      <div className="left-part">
        <ImageProfil id={comment.userId} />
      </div>
      <div className="right-part">
        <div className="comment-header">
          <h3>
            {!isEmpty(usersData[0]) && userName(comment.userId)}
            {comment?.author === comment?.userId ? (
              <span className="author"> Auteur </span>
            ) : null}
          </h3>
          <span>{DateParser(comment.createdAt)}</span>
        </div>
        <p className="comment-content">
          {parse(parseMention(comment.commentaire || ""))}
        </p>
        <div className="comment-actions">
          {!isReply && editingId !== comment.id ? (
            <button
              type="button"
              className="comment-reply-btn"
              onClick={() => {
                setReplyTo(replyTo === comment.id ? null : comment.id);
                setReplyText("");
              }}
            >
              <CornerDownRight size={13} /> Répondre
            </button>
          ) : null}
          {isAdmin || userData.id === comment.userId ? (
            <UpdateComment
              comment={comment}
              postId={post.id}
              onEditChange={(editing) => setEditingId(editing ? comment.id : null)}
            />
          ) : null}
        </div>
      </div>
    </div>
  );

  const mentionData = usersData.map((user) => ({
    id: user.id,
    display: `${user.firstname} ${user.name}`.trim(),
  }));

  return (
    <div className="comments-container">
      {topComments.map((comment) => (
        <div className="comment-thread" key={comment.id}>
          {renderComment(comment)}

          {repliesOf(comment.id).length > 0 ? (
            <div className="comment-replies">
              {repliesOf(comment.id).map((reply) => renderComment(reply, true))}
            </div>
          ) : null}
        </div>
      ))}

      <div className="commentForm">
        <div className="commentForm-picture">
          <ImageProfil id={userData.id} />
        </div>
        <div className="commentForm-form">
          <form action="" onSubmit={handleComment} className="comment-form">
            <MentionsInput
              className="custom-comment-form"
              value={commentaire}
              onChange={(event, newValue) => setComment(newValue)}
              placeholder="Poster un commentaire..."
              suggestionsPortalHost={document.body}
            >
              <Mention
                trigger="@"
                className="mention-tag"
                data={mentionData}
                renderSuggestion={(
                  suggestion,
                  search,
                  highlightedDisplay,
                  index,
                  focused
                ) => (
                  <div className={`mention-suggestion${focused ? " focused" : ""}`}>
                    <ImageProfil id={suggestion.id} />
                    <span className="mention-suggestion-name">{highlightedDisplay}</span>
                  </div>
                )}
              />
            </MentionsInput>
            <br />
            <input type="submit" value="Commenter" className="inputCommentaire" />
          </form>
        </div>
      </div>

      {replyComment &&
        createPortal(
          <div
            className="reply-modal-overlay"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeReply();
            }}
          >
            <div className="reply-modal" role="dialog" aria-label="Répondre au commentaire">
              <div className="reply-modal-head">
                <span className="reply-modal-title">
                  <CornerDownRight size={16} /> Répondre à {userName(replyComment.userId)}
                </span>
                <button className="reply-modal-close" onClick={closeReply} aria-label="Fermer">
                  <X size={16} />
                </button>
              </div>

              <div className="reply-modal-quote">
                <ImageProfil id={replyComment.userId} />
                <div className="reply-modal-quote-text">
                  <strong>{userName(replyComment.userId)}</strong>
                  <p>{parse(parseMention(replyComment.commentaire || ""))}</p>
                </div>
              </div>

              <form className="reply-modal-form" onSubmit={(e) => handleReply(e, replyComment.id)}>
                <MentionsInput
                  className="custom-comment-form"
                  value={replyText}
                  onChange={(event, newValue) => setReplyText(newValue)}
                  placeholder="Votre réponse…  (tapez @ pour mentionner)"
                  autoFocus
                  suggestionsPortalHost={document.body}
                >
                  <Mention
                    trigger="@"
                    className="mention-tag"
                    data={mentionData}
                    renderSuggestion={(suggestion, search, highlightedDisplay, index, focused) => (
                      <div className={`mention-suggestion${focused ? " focused" : ""}`}>
                        <ImageProfil id={suggestion.id} />
                        <span className="mention-suggestion-name">{highlightedDisplay}</span>
                      </div>
                    )}
                  />
                </MentionsInput>
                <div className="reply-modal-actions">
                  <button type="button" className="reply-modal-cancel" onClick={closeReply}>
                    Annuler
                  </button>
                  <input
                    type="submit"
                    value="Répondre"
                    className="inputCommentaire"
                    disabled={!replyText.trim()}
                  />
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CardComments;
